/**
 * Claims queue + fulfillment console endpoints (§15.2 "Winners and claims", §16.2). Request/response shapes come
 * from @windrop/contracts; the thin client adds cookies, Idempotency-Key and problem+json handling.
 */
import type {
  AppealDecisionRequest,
  ApproveClaimRequest,
  ClaimAdminDetailDto,
  ClaimEvidenceAccessResponse,
  ClaimQueueQuery,
  ClaimQueueRowDto,
  CompleteFulfillmentRequest,
  FulfillmentAdminDto,
  FulfillmentExceptionRequest,
  FulfillmentListQuery,
  LegalHoldRequest,
  PageInfo,
  RecordVerificationRequest,
  RejectClaimRequest,
  RequestInfoRequest,
  StartFulfillmentRequest,
  UpdatePayoutDestinationRequest,
} from '@windrop/contracts';
import { api, type QueryValue } from './client';

const q = (o: object): Record<string, QueryValue> =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== '')) as Record<
    string,
    QueryValue
  >;
const seg = encodeURIComponent;

export const claimsApi = {
  list: (query: Partial<ClaimQueueQuery> = {}) =>
    api.get<{ items: ClaimQueueRowDto[]; page: PageInfo }>('/v1/admin/claims', q(query)),
  get: (id: string) => api.get<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}`),
  recordVerification: (id: string, body: RecordVerificationRequest) =>
    api.post<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}/verification`, body),
  requestInfo: (id: string, body: RequestInfoRequest) =>
    api.post<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}/request-info`, body),
  approve: (id: string, body: ApproveClaimRequest = {}) =>
    api.post<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}/approve`, body),
  reject: (id: string, body: RejectClaimRequest) =>
    api.post<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}/reject`, body),
  decideAppeal: (id: string, body: AppealDecisionRequest) =>
    api.post<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}/appeal`, body),
  payoutDestination: (id: string, body: UpdatePayoutDestinationRequest) =>
    api.post<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}/payout-destination`, body),
  legalHold: (id: string, body: LegalHoldRequest) =>
    api.post<ClaimAdminDetailDto>(`/v1/admin/claims/${seg(id)}/legal-hold`, body),
  evidenceAccess: (id: string, evidenceId: string, purpose: string) =>
    api.post<ClaimEvidenceAccessResponse>(`/v1/admin/claims/${seg(id)}/evidence/${seg(evidenceId)}/access`, {
      purpose,
    }),
};

export const fulfillmentApi = {
  list: (query: Partial<FulfillmentListQuery> = {}) =>
    api.get<{ items: FulfillmentAdminDto[]; page: PageInfo }>('/v1/admin/fulfillment', q(query)),
  start: (id: string, body: StartFulfillmentRequest) =>
    api.post<FulfillmentAdminDto>(`/v1/admin/fulfillment/${seg(id)}/start`, body),
  exception: (id: string, body: FulfillmentExceptionRequest) =>
    api.post<FulfillmentAdminDto>(`/v1/admin/fulfillment/${seg(id)}/exception`, body),
  complete: (id: string, body: CompleteFulfillmentRequest) =>
    api.post<FulfillmentAdminDto>(`/v1/admin/fulfillment/${seg(id)}/complete`, body),
};
